import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';

interface FlagModalProps {
  open: boolean;
  itemType: 'home' | 'conversation' | 'message';
  itemName: string;
  onClose: () => void;
  onFlag: (reason: string, customReason?: string) => Promise<{ success: boolean; error?: string }>;
}

const FLAG_REASONS = {
  home: [
    'Inappropriate images',
    'Spam listing',
    'Misleading information',
    'Duplicate property',
    'Privacy violation',
    'Offensive content',
    'Suspected fraud',
    'Copyright violation',
    'Other',
  ],
  conversation: [
    'Inappropriate language',
    'Harassment',
    'Spam',
    'Offensive content',
    'Scam or fraud',
    'Privacy violation',
    'Other',
  ],
  message: [
    'Inappropriate content',
    'Harassment',
    'Spam',
    'Offensive language',
    'Other',
  ],
};

/**
 * FlagModal - Modal for flagging content with reason selection
 *
 * Features:
 * - Predefined reason options per content type
 * - Custom reason text field
 * - Validation
 */
export default function FlagModal({
  open,
  itemType,
  itemName,
  onClose,
  onFlag,
}: FlagModalProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setSelectedReason('');
    setCustomReason('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedReason) return;

    setIsSubmitting(true);
    try {
      const finalReason = selectedReason === 'Other' && customReason ? customReason : selectedReason;
      await onFlag(finalReason, selectedReason === 'Other' ? customReason : undefined);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const reasons = FLAG_REASONS[itemType] || FLAG_REASONS.home;
  const isValid = selectedReason && (selectedReason !== 'Other' || customReason.trim().length > 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon color="error" />
          Flag {itemType === 'home' ? 'Home' : itemType === 'conversation' ? 'Conversation' : 'Message'}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Flagging: <strong>{itemName}</strong>
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">Select a reason for flagging:</FormLabel>
          <RadioGroup
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
            sx={{ mt: 2 }}
          >
            {reasons.map((reason) => (
              <FormControlLabel
                key={reason}
                value={reason}
                control={<Radio />}
                label={reason}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {selectedReason === 'Other' && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Please specify the reason"
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            placeholder="Describe the issue in detail..."
            sx={{ mt: 2 }}
            required
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={!isValid || isSubmitting}
          startIcon={<FlagIcon />}
        >
          {isSubmitting ? 'Flagging...' : 'Flag Content'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
